import React, { useEffect } from 'react';
import 'twin.macro';
import {
  SparklesIcon,
  XMarkIcon,
  AdjustmentsHorizontalIcon,
  DocumentIcon,
  NoSymbolIcon,
} from '@heroicons/react/24/outline';
import Button from 'components/Buttons/Button';
import { useTranslation } from 'react-i18next';
import { ImprovementType } from '../../types';
import { usePostHog } from 'posthog-js/react';
import { useAIGenerationContext } from 'components/Editor/AIAssist/contexts/AIGenerationContext';
import { trackEvent } from 'queries/user/track';
import { Menu } from '@teetsh/app/src/components/Menu';
import { MenuContext } from '@teetsh/app/src/components/Menu/useMenuContext';

interface Props {
  onSelect: (type: ImprovementType) => void;
  onClose: () => void;
  isLoading?: boolean;
  currentAction?: ImprovementType;
  onMenuOpenChange?: (isOpen: boolean) => void;
}

function MenuOpenTracker({
  onOpenChange,
}: {
  onOpenChange?: (isOpen: boolean) => void;
}) {
  const { open } = React.useContext(MenuContext);

  useEffect(() => {
    onOpenChange?.(open || false);
  }, [open, onOpenChange]);

  return null;
}

export function SelectionToolbarButtons({
  onSelect,
  onClose,
  isLoading,
  currentAction,
  onMenuOpenChange,
}: Props) {
  const { t } = useTranslation(['common']);
  const posthog = usePostHog();

  const isImproveWritingLoading =
    isLoading && currentAction === 'improveWriting';
  const isFixGrammarLoading = isLoading && currentAction === 'fixGrammar';
  const isGeneratePdfLoading = isLoading && currentAction === 'generatePdf';
  let trackingContext: string | undefined;
  let onPauseForSessionHandler: (() => void) | undefined;
  let onPauseUntilReactivateHandler: (() => void) | undefined;
  try {
    const context = useAIGenerationContext();
    trackingContext = context.trackingContext;
    onPauseForSessionHandler = context.onPauseForSession;
    onPauseUntilReactivateHandler = context.onPauseUntilReactivate;
  } catch {
    // Context not available, tracking will be skipped
  }

  return (
    <div tw="bg-white shadow rounded-lg p-2 flex items-center gap-2 z-50">
      <Button
        small
        outlined
        icon={SparklesIcon}
        loading={isImproveWritingLoading}
        disabled={isLoading}
        tw="shrink-0"
        onClick={() => {
          posthog?.startSessionRecording(true);
          onSelect('improveWriting');
        }}
      >
        {t('buttons.improveWriting')}
      </Button>
      <Button
        small
        outlined
        icon={SparklesIcon}
        loading={isFixGrammarLoading}
        disabled={isLoading}
        onClick={() => {
          posthog?.startSessionRecording(true);
          onSelect('fixGrammar');
        }}
      >
        {t('buttons.fixSpelling')}
      </Button>
      <Button
        small
        outlined
        loading={isGeneratePdfLoading}
        disabled={isLoading}
        onClick={() => {
          if (trackingContext) {
            trackEvent('Editor:ExportPDF:start', {
              context: trackingContext,
            });
          }
          posthog?.startSessionRecording(true);
          onSelect('generatePdf');
        }}
      >
        {t('buttons.convertToPDF')}
      </Button>
      {(onPauseForSessionHandler || onPauseUntilReactivateHandler) && (
        <Menu portal>
          <MenuOpenTracker onOpenChange={onMenuOpenChange} />
          <Menu.Button
            minimal
            small
            icon={AdjustmentsHorizontalIcon}
            aria-label={t('buttons.configureContextualActions')}
          />
          <Menu.Items>
            <div tw="px-4 py-2 text-sm text-neutral-quiet">
              {t('buttons.configureContextualActions')}
            </div>
            <Menu.Item
              icon={DocumentIcon}
              onClick={() => {
                if (trackingContext) {
                  trackEvent('Editor:AI:PauseForSession', {
                    context: trackingContext,
                  });
                }
                onPauseForSessionHandler?.();
              }}
              text={t('buttons.pauseForThisSession')}
            />
            <Menu.Item
              icon={NoSymbolIcon}
              onClick={() => {
                if (trackingContext) {
                  trackEvent('Editor:AI:PauseUntilReactivate', {
                    context: trackingContext,
                  });
                }
                onPauseUntilReactivateHandler?.();
              }}
              text={t('buttons.pauseUntilReactivate')}
            />
          </Menu.Items>
        </Menu>
      )}
      <Button
        minimal
        small
        icon={XMarkIcon}
        onClick={() => onClose()}
        aria-label={t('buttons.close')}
        tw="p-1.5"
      />
    </div>
  );
}
